import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

/** A field in error. A button, such as a date picker's, may not carry aria-invalid, so it marks itself with data-invalid. */
export const invalidFieldSelector = '[aria-invalid="true"], [data-invalid="true"]'

/**
 * Focuses the first field in error inside `container` that can take focus, passing over a group
 * such as a question's fieldset; false when there is none.
 */
export function focusFirstInvalid(container: HTMLElement): boolean {
  for (const field of container.querySelectorAll<HTMLElement>(invalidFieldSelector)) {
    field.focus()
    if (document.activeElement === field) {
      return true
    }
  }
  return false
}

/**
 * For a dialog: once one of its actions is pressed, the first field in `content` that then shows
 * an error takes focus, as a refused Save moves focus to the field to fix (WCAG 3.3.1, 2.4.3).
 * An error shown after a request counts too, until focus moves into `content` some other way or
 * the dialog starts closing, as on Cancel. Returns the handler for the actions' clicks.
 */
export function useRevealInvalidAfterAction(content: HTMLElement | null, open: boolean): () => void {
  const isPending = useRef(false)
  const isOpen = useRef(open)
  const queued = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const reveal = useCallback(() => {
    if (isPending.current && isOpen.current && content !== null && focusFirstInvalid(content)) {
      isPending.current = false
    }
  }, [content])

  // A closing dialog hands focus back to what opened it, so nothing pending may take it; set in
  // the commit, so a Cancel's close lands before the look its press queued.
  useLayoutEffect(() => {
    isOpen.current = open
    if (!open) {
      isPending.current = false
      clearTimeout(queued.current)
    }
  }, [open])
  useLayoutEffect(() => () => clearTimeout(queued.current), [])

  useEffect(() => {
    if (content === null) {
      return undefined
    }
    const observer = new MutationObserver(reveal)
    observer.observe(content, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-invalid', 'data-invalid'] })
    // Focus entering the content, the person's or the reveal's own, ends the wait.
    const moved = () => {
      isPending.current = false
    }
    content.addEventListener('focusin', moved)
    return () => {
      observer.disconnect()
      content.removeEventListener('focusin', moved)
    }
  }, [content, reveal])

  return useCallback(() => {
    isPending.current = true
    // An error already shown is not drawn again, so this looks once the press has been handled.
    clearTimeout(queued.current)
    queued.current = setTimeout(reveal, 0)
  }, [reveal])
}
